import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { notify } from '../../notifications';
import { apiErrorMessage } from './edit/editApi';
import { ConfirmDialog, EditEmptyState } from './edit/EditCommon';
import {
    createTerminal,
    deleteTerminal,
    DeliveryPharmacy,
    fetchPharmacies,
    fetchTerminals,
    TerminalFields,
    updateTerminal
} from './delivery/deliveryApi';
import type { CityTerminal } from './delivery/deliveryApi';

interface CityManagerDialogProps {
    open: boolean;
    onClose: () => void;
    /** Вызывается после добавления, изменения и удаления, чтобы экран сверки обновил свой список терминалов */
    onChanged?: () => void;
}

const EMPTY_FIELDS: TerminalFields = { city_name: '', terminal_code: '', terminal_id: '', address: '' };

/** Предельные длины полей совпадают с проверкой на сервере */
const MAX_LENGTH = { city_name: 100, terminal_code: 50, terminal_id: 100, address: 999 };

const headCellSx = {
    backgroundColor: '#f8fafc',
    color: '#475569',
    fontWeight: 700,
    fontSize: '0.78rem',
    borderBottom: '1px solid #e2e8f0',
    whiteSpace: 'nowrap' as const
};

const bodyCellSx = { fontSize: '0.86rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' };

const pharmacyLabel = (p: DeliveryPharmacy): string =>
    p.pharmacyNum ? `${p.username} (Аптека: ${p.pharmacyNum})` : p.username;

const formatDate = (value: string): string => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const sortTerminals = (list: CityTerminal[]): CityTerminal[] =>
    list.slice().sort((a, b) => a.city_name.localeCompare(b.city_name, 'ru') || a.id - b.id);

const CityManagerDialog: React.FC<CityManagerDialogProps> = ({ open, onClose, onChanged }) => {
    const [terminals, setTerminals] = useState<CityTerminal[]>([]);
    const [pharmacies, setPharmacies] = useState<DeliveryPharmacy[]>([]);
    const [loading, setLoading] = useState(false);

    const [selectedPharmacy, setSelectedPharmacy] = useState<DeliveryPharmacy | null>(null);
    const [fields, setFields] = useState<TerminalFields>(EMPTY_FIELDS);
    const [editing, setEditing] = useState<CityTerminal | null>(null);
    const [saving, setSaving] = useState(false);

    const [search, setSearch] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<CityTerminal | null>(null);
    const [deleting, setDeleting] = useState(false);

    // Данные грузятся только при открытии окна; при закрытии форма и поиск сбрасываются
    useEffect(() => {
        if (!open) {
            setEditing(null);
            setFields(EMPTY_FIELDS);
            setSelectedPharmacy(null);
            setSearch('');
            setDeleteTarget(null);
            return;
        }

        let cancelled = false;
        setLoading(true);
        Promise.all([fetchTerminals(), fetchPharmacies()])
            .then(([terminalList, pharmacyList]) => {
                if (cancelled) return;
                setTerminals(terminalList);
                setPharmacies(pharmacyList);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Error loading city manager data:', error);
                notify.error(apiErrorMessage(error, 'Не удалось загрузить данные о городах'));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open]);

    const visibleTerminals = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return terminals;
        return terminals.filter((t) =>
            [t.city_name, t.terminal_code, t.terminal_id, t.address, t.created_by]
                .some((value) => String(value || '').toLowerCase().indexOf(query) !== -1)
        );
    }, [terminals, search]);

    const isFilled = Object.keys(fields).every((key) => fields[key as keyof TerminalFields].trim() !== '');
    const canSubmit = isFilled && !saving && (editing !== null || selectedPharmacy !== null);

    const setField = (key: keyof TerminalFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFields((prev) => ({ ...prev, [key]: event.target.value }));
    };

    const resetForm = () => {
        setEditing(null);
        setFields(EMPTY_FIELDS);
    };

    const startEdit = (terminal: CityTerminal) => {
        setEditing(terminal);
        setFields({
            city_name: terminal.city_name,
            terminal_code: terminal.terminal_code,
            terminal_id: terminal.terminal_id,
            address: terminal.address || ''
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) return;

        setSaving(true);
        try {
            if (editing) {
                const updated = await updateTerminal(editing.id, fields);
                setTerminals((prev) => sortTerminals(prev.map((t) => (t.id === updated.id ? updated : t))));
                notify.success('Город обновлен');
            } else if (selectedPharmacy) {
                const created = await createTerminal(selectedPharmacy.username, fields);
                setTerminals((prev) => sortTerminals(prev.concat(created)));
                notify.success('Город добавлен');
            }
            resetForm();
            if (onChanged) onChanged();
        } catch (error) {
            console.error('Error saving city terminal:', error);
            notify.error(apiErrorMessage(error, editing ? 'Не удалось обновить город' : 'Не удалось добавить город'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;

        setDeleting(true);
        try {
            await deleteTerminal(deleteTarget.id);
            const removedId = deleteTarget.id;
            setTerminals((prev) => prev.filter((t) => t.id !== removedId));
            if (editing && editing.id === removedId) resetForm();
            setDeleteTarget(null);
            notify.success('Город удален');
            if (onChanged) onChanged();
        } catch (error) {
            console.error('Error deleting city terminal:', error);
            notify.error(apiErrorMessage(error, 'Не удалось удалить город'));
        } finally {
            setDeleting(false);
        }
    };

    // В режиме редактирования аптека берется из записи и не меняется
    const pharmacyValue: DeliveryPharmacy | null = editing
        ? pharmacies.filter((p) => p.username === editing.created_by)[0] || { username: editing.created_by, pharmacyNum: '' }
        : selectedPharmacy;

    return (
        <>
            <Dialog
                open={open}
                onClose={saving ? undefined : onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle
                    sx={{
                        backgroundColor: '#ffffff',
                        borderBottom: '1px solid #e2e8f0',
                        fontSize: '1.05rem',
                        fontWeight: 700,
                        color: '#0f172a'
                    }}
                >
                    Управление городами
                </DialogTitle>

                <DialogContent sx={{ px: 3 }}>
                    <Box component="form" onSubmit={handleSubmit} sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Autocomplete
                            options={pharmacies}
                            value={pharmacyValue}
                            disabled={editing !== null || saving}
                            getOptionLabel={pharmacyLabel}
                            isOptionEqualToValue={(option, value) => option.username === value.username}
                            onChange={(_event, value) => setSelectedPharmacy(value)}
                            noOptionsText="Аптеки не найдены"
                            size="small"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Аптека"
                                    helperText={editing ? 'Аптеку у существующей записи изменить нельзя' : undefined}
                                />
                            )}
                        />

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField
                                label="Название города"
                                value={fields.city_name}
                                onChange={setField('city_name')}
                                disabled={saving}
                                size="small"
                                inputProps={{ maxLength: MAX_LENGTH.city_name }}
                                sx={{ flex: '1 1 200px' }}
                            />
                            <TextField
                                label="Код города"
                                value={fields.terminal_code}
                                onChange={setField('terminal_code')}
                                disabled={saving}
                                size="small"
                                inputProps={{ maxLength: MAX_LENGTH.terminal_code }}
                                sx={{ flex: '1 1 150px' }}
                            />
                            <TextField
                                label="ID терминала"
                                value={fields.terminal_id}
                                onChange={setField('terminal_id')}
                                disabled={saving}
                                size="small"
                                inputProps={{ maxLength: MAX_LENGTH.terminal_id }}
                                sx={{ flex: '1 1 150px' }}
                            />
                        </Box>

                        <TextField
                            label="Адрес аптеки"
                            value={fields.address}
                            onChange={setField('address')}
                            disabled={saving}
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: MAX_LENGTH.address }}
                            placeholder="Например: г.Актау, Мангистауская область, 15 мкр, д.78"
                            helperText="Точный адрес для жесткого сравнения с данными"
                        />

                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            {editing && (
                                <Button
                                    onClick={resetForm}
                                    disabled={saving}
                                    sx={{ textTransform: 'none', fontWeight: 600, color: '#475569' }}
                                >
                                    Отмена
                                </Button>
                            )}
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={!canSubmit}
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none', minWidth: 130 }}
                            >
                                {saving ? <CircularProgress size={20} color="inherit" /> : editing ? 'Сохранить' : 'Добавить'}
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 3, mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>
                            Города и терминалы
                            <Typography component="span" sx={{ ml: 1, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 600 }}>
                                {visibleTerminals.length === terminals.length
                                    ? terminals.length
                                    : `${visibleTerminals.length} из ${terminals.length}`}
                            </Typography>
                        </Typography>
                        <TextField
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Поиск по городу, ID, адресу, аптеке"
                            size="small"
                            sx={{ width: { xs: '100%', sm: 340 } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                    {loading ? (
                        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : visibleTerminals.length === 0 ? (
                        <EditEmptyState
                            icon={<LocationCityOutlinedIcon />}
                            title={terminals.length === 0 ? 'Городов пока нет' : 'Ничего не найдено'}
                            text={
                                terminals.length === 0
                                    ? 'Выберите аптеку, заполните поля выше и нажмите «Добавить»'
                                    : 'Измените запрос поиска'
                            }
                        />
                    ) : (
                        <TableContainer sx={{ maxHeight: 380, border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={headCellSx}>Город</TableCell>
                                        <TableCell sx={headCellSx}>Код</TableCell>
                                        <TableCell sx={headCellSx}>ID</TableCell>
                                        <TableCell sx={{ ...headCellSx, minWidth: 220 }}>Адрес</TableCell>
                                        <TableCell sx={headCellSx}>Аптека</TableCell>
                                        <TableCell sx={headCellSx}>Дата создания</TableCell>
                                        <TableCell sx={{ ...headCellSx, width: 96 }} align="right">Действия</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleTerminals.map((terminal) => (
                                        <TableRow
                                            key={terminal.id}
                                            hover
                                            selected={editing !== null && editing.id === terminal.id}
                                        >
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{terminal.city_name}</TableCell>
                                            <TableCell sx={bodyCellSx}>{terminal.terminal_code}</TableCell>
                                            <TableCell sx={bodyCellSx}>{terminal.terminal_id}</TableCell>
                                            <TableCell sx={{ ...bodyCellSx, minWidth: 220 }}>{terminal.address || '—'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{terminal.created_by}</TableCell>
                                            <TableCell sx={{ ...bodyCellSx, color: '#64748b', whiteSpace: 'nowrap' }}>
                                                {formatDate(terminal.created_at)}
                                            </TableCell>
                                            <TableCell sx={bodyCellSx} align="right">
                                                <Tooltip title="Изменить">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => startEdit(terminal)}
                                                            disabled={saving}
                                                            sx={{ color: '#475569' }}
                                                        >
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Удалить">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setDeleteTarget(terminal)}
                                                            disabled={saving}
                                                            sx={{ color: '#b91c1c' }}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid #e2e8f0' }}>
                    <Button
                        onClick={onClose}
                        disabled={saving}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#475569' }}
                    >
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={deleteTarget !== null}
                title="Удаление города"
                confirmText="Удалить"
                loading={deleting}
                maxWidth="xs"
                onConfirm={handleDelete}
                onClose={() => setDeleteTarget(null)}
            >
                <Typography sx={{ fontSize: '0.9rem', color: '#334155' }}>
                    {deleteTarget
                        ? `Запись «${deleteTarget.city_name}» (ID ${deleteTarget.terminal_id}, аптека ${deleteTarget.created_by}) будет удалена без возможности восстановления`
                        : ''}
                </Typography>
            </ConfirmDialog>
        </>
    );
};

export default CityManagerDialog;
